import { apiPrefix } from './tenant'

/**
 * 微信内分享卡片：微信不读 og 标签、也不抓网页图片（2017 年起），没接 JS-SDK 的页面分享出去只有默认样式。
 * 这里在微信内置浏览器里加载 jweixin，向服务端要签名做 wx.config，再把当前分享数据（标题 / 摘要 / 链接 / 封面）
 * 喂给 updateAppMessageShareData（发给朋友）与 updateTimelineShareData（朋友圈）。
 *
 * - 服务端未配置 / 管理员没开：签名端点 404，这里静默不加载 SDK。
 * - SPA 内切卡：share-meta.js 每次更新分享标签都会调 setWechatShare；ready 之后随时可重设。
 * - 签名的 url：iOS 用首次进入页面的 URL（去掉 #）即可；Android 微信不支持 pushState，URL 变了要用当前 URL 重新 config。
 */

const SDK_URLS = ['https://res.wx.qq.com/open/js/jweixin-1.6.0.js', 'https://res2.wx.qq.com/open/js/jweixin-1.6.0.js']
const JS_API_LIST = ['updateAppMessageShareData', 'updateTimelineShareData']

export const inWechat = () => typeof navigator !== 'undefined' && /MicroMessenger/i.test(navigator.userAgent)
const isAndroid = () => /Android/i.test(navigator.userAgent)

/**
 * 页面首次加载时的地址（模块求值早于任何 navigate / pushState）。iOS 微信用它校验签名——
 * 站点一进页面就会把 /post?note-id=X 改写成 ?note-id=X&active-note-id=X，若拿改写后的地址去签，iOS 判 invalid signature。
 */
const ENTRY_URL = typeof window !== 'undefined' ? window.location.href.split('#')[0] : ''
/** ?wxdebug=1 或 localStorage.wxdebug='1'：打开 wx.config 的 debug（微信会 alert 每个接口的结果，真机排查用） */
const debugOn = () => {
    try {
        return /[?&]wxdebug=1/.test(window.location.search) || window.localStorage.getItem('wxdebug') === '1'
    } catch {
        return false
    }
}

let sdkPromise = null
let ready = false
let disabled = false
let configuredUrl = ''
let pending = null

const loadSdk = () => {
    if (sdkPromise) return sdkPromise
    sdkPromise = new Promise((resolve, reject) => {
        const tryAt = (i) => {
            if (window.wx && window.wx.config) return resolve(window.wx)
            if (i >= SDK_URLS.length) return reject(new Error('jweixin load failed'))
            const s = document.createElement('script')
            s.src = SDK_URLS[i]
            s.async = true
            s.onload = () => (window.wx && window.wx.config ? resolve(window.wx) : tryAt(i + 1))
            s.onerror = () => tryAt(i + 1)
            document.head.appendChild(s)
        }
        tryAt(0)
    })
    return sdkPromise
}

/** 签名用的地址：iOS 恒为入口地址；Android 用当前地址（它不认 pushState，地址变了要重签） */
const signUrl = () => (isAndroid() ? window.location.href.split('#')[0] : ENTRY_URL)

const fetchSignature = async (url) => {
    const res = await fetch(apiPrefix() + '/v1/wx/sign?url=' + encodeURIComponent(url), { cache: 'no-store' })
    if (res.status === 404) {
        disabled = true
        return null
    }
    if (!res.ok) throw new Error('sign ' + res.status)
    return res.json()
}

const applyPending = () => {
    if (!ready || !pending || !window.wx) return
    const { title, desc, link, imgUrl } = pending
    try {
        window.wx.updateAppMessageShareData({ title, desc, link, imgUrl })
        window.wx.updateTimelineShareData({ title, link, imgUrl })
    } catch (e) {
        console.warn('[wx] share data', e)
    }
}

let configuring = null
let lastError = null
/** 排查用：window.__wxShare() 看当前状态 */
if (typeof window !== 'undefined') {
    window.__wxShare = () => ({ inWechat: inWechat(), disabled, ready, configuredUrl, entryUrl: ENTRY_URL, pending, lastError })
}

const configure = () => {
    if (disabled) return Promise.resolve()
    const url = signUrl()
    if (configuring) return configuring
    configuring = (async () => {
        const sig = await fetchSignature(url)
        if (!sig) return
        const wx = await loadSdk()
        ready = false
        configuredUrl = url
        await new Promise((resolve) => {
            wx.config({ debug: debugOn(), appId: sig.appId, timestamp: sig.timestamp, nonceStr: sig.nonceStr, signature: sig.signature, jsApiList: JS_API_LIST })
            wx.ready(() => {
                ready = true
                applyPending()
                resolve()
            })
            wx.error((err) => {
                console.warn('[wx] config error', err)
                lastError = err
                resolve()
            })
        })
    })().catch((e) => {
        console.warn('[wx] init failed', e && e.message)
    }).finally(() => {
        configuring = null
    })
    return configuring
}

/**
 * 设置当前页面的微信分享内容。非微信环境 / 未配置时是空操作。
 * Android 上 URL 变了（SPA 切卡）需要重新签名 config；iOS 用首次进入的 URL 一直有效。
 */
export const setWechatShare = ({ title, description, url, image }) => {
    if (!inWechat()) return
    pending = { title: title || document.title, desc: description || '', link: url || window.location.href.split('#')[0], imgUrl: image || '' }
    if (disabled) return
    const needConfig = !configuredUrl || (isAndroid() && signUrl() !== configuredUrl)
    if (needConfig) {
        void configure()
    } else {
        applyPending()
    }
}
